// ============================================================
// Add / Edit Staff Member Page — Staff Management
// ============================================================

import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Grid,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from '@/router/paths';
import {
  useDepartments,
  useDesignations,
  useSchoolRoles,
  useStaffById,
  useCreateStaff,
  useUpdateStaff,
} from '@/hooks/useStaffManagement';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ArrowLeft, Save, User, Briefcase, PhoneCall } from 'lucide-react';
import toast from 'react-hot-toast';

export function AddEditStaffPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const { data: staffData, isLoading: loadingStaff } = useStaffById(id);
  const { data: departments = [] } = useDepartments();
  const { data: designations = [] } = useDesignations();
  const { data: roles = [], isLoading: loadingRoles } = useSchoolRoles();

  const createMutation = useCreateStaff();
  const updateMutation = useUpdateStaff();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role_id: '',
    employee_code: '',
    phone: '',
    gender: 'Male',
    date_of_birth: '',
    photo_url: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    blood_group: 'O+',
    joining_date: new Date().toISOString().split('T')[0],
    department_id: '',
    designation_id: '',
    qualification: '',
    experience_years: 0,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && staffData) {
      const u = staffData.user || {};
      const deptId = staffData.department?.id || staffData.department_id || staffData.departmentId || '';
      const desigId = staffData.designation?.id || staffData.designation_id || staffData.designationId || '';
      const roleId = u.role_id || staffData.role?.id || staffData.role_id || staffData.roleId || '';

      const rawGender = String(staffData.gender || u.gender || 'Female').toUpperCase();
      let genderVal = 'Female';
      if (rawGender.startsWith('M')) genderVal = 'Male';
      else if (rawGender.startsWith('F')) genderVal = 'Female';
      else if (rawGender.startsWith('O')) genderVal = 'Other';

      setFormData({
        first_name: u.first_name || staffData.first_name || staffData.firstName || '',
        last_name: u.last_name || staffData.last_name || staffData.lastName || '',
        email: u.email || staffData.email || '',
        role_id: roleId,
        employee_code: staffData.employee_code || staffData.employeeCode || '',
        phone: u.phone || staffData.phone || staffData.mobile || '',
        gender: genderVal,
        date_of_birth: staffData.date_of_birth ? staffData.date_of_birth.split('T')[0] : '',
        photo_url: staffData.photo_url || staffData.photoUrl || '',
        address: staffData.address || '',
        emergency_contact_name: staffData.emergency_contact_name || '',
        emergency_contact_phone: staffData.emergency_contact_phone || '',
        blood_group: staffData.blood_group || 'O+',
        joining_date: staffData.joining_date ? staffData.joining_date.split('T')[0] : '',
        department_id: deptId,
        designation_id: desigId,
        qualification: staffData.qualification || '',
        experience_years: staffData.experience_years || staffData.experienceYears || 0,
      });
    }
  }, [isEdit, staffData]);

  const filteredDesignations = formData.department_id
    ? (designations || []).filter(
        (d) =>
          d.department_id === formData.department_id ||
          d.departmentId === formData.department_id ||
          d.department?.id === formData.department_id
      )
    : designations || [];

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'department_id' && prev.department_id !== value) {
        next.designation_id = ''; // Reset designation when department changes
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email address is required';
    if (!formData.role_id) newErrors.role_id = 'Role is required';
    if (!formData.employee_code.trim()) newErrors.employee_code = 'Employee code is required';
    if (!formData.department_id) newErrors.department_id = 'Department is required';
    if (!formData.designation_id) newErrors.designation_id = 'Designation is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please check required fields');
      return;
    }

    const cleanVal = (val) => {
      if (val === undefined || val === null) return null;
      if (typeof val === 'string') {
        const trimmed = val.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      return val;
    };

    const payload = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      email: formData.email.trim(),
      role_id: formData.role_id,
      employee_code: formData.employee_code.trim(),
      phone: cleanVal(formData.phone),
      gender: formData.gender,
      date_of_birth: cleanVal(formData.date_of_birth),
      photo_url: cleanVal(formData.photo_url),
      address: cleanVal(formData.address),
      emergency_contact_name: cleanVal(formData.emergency_contact_name),
      emergency_contact_phone: cleanVal(formData.emergency_contact_phone),
      blood_group: cleanVal(formData.blood_group),
      joining_date: cleanVal(formData.joining_date),
      department_id: formData.department_id,
      designation_id: formData.designation_id,
      qualification: cleanVal(formData.qualification),
      experience_years: formData.experience_years ? Number(formData.experience_years) || 0 : 0,
    };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id, ...payload });
        toast.success('Staff member updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Staff member added successfully');
      }
      navigate(PATHS.STAFF_MANAGEMENT_STAFF);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      let errorMsg = err.response?.data?.message || err.message || 'Failed to save staff member';
      if (Array.isArray(errorDetail)) {
        errorMsg = errorDetail.map((d) => d.msg || d.message).join(' | ');
      } else if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      }
      toast.error(errorMsg);
    }
  };

  if (isEdit && loadingStaff) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const loading = createMutation.isPending || updateMutation.isPending;

  return (
    <Box>
      <PageHeader
        title={isEdit ? 'Edit Staff Member' : 'Add New Staff Member'}
        subtitle={
          isEdit
            ? 'Update personal, contact, and employment details for this staff member'
            : 'Enroll a new employee and assign them to a department, designation, and employee type'
        }
        actions={
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => navigate(PATHS.STAFF_MANAGEMENT_STAFF)}
            sx={{ bgcolor: 'white' }}
          >
            Back to Directory
          </Button>
        }
      />

      <form onSubmit={handleSubmit}>
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack spacing={4}>
            {/* Section 1: Personal & Contact Information */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <User size={18} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Personal & Contact Information
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="First Name"
                    placeholder="e.g. Anjali"
                    fullWidth
                    required
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    error={!!errors.first_name}
                    helperText={errors.first_name}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Last Name"
                    placeholder="e.g. Sharma"
                    fullWidth
                    required
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    error={!!errors.last_name}
                    helperText={errors.last_name}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    select
                    label="Gender"
                    fullWidth
                    value={formData.gender}
                    onChange={(e) => handleChange('gender', e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Date of Birth"
                    type="date"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={formData.date_of_birth}
                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    select
                    label="Blood Group"
                    fullWidth
                    value={formData.blood_group}
                    onChange={(e) => handleChange('blood_group', e.target.value)}
                    disabled={loading}
                  >
                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((bg) => (
                      <MenuItem key={bg} value={bg}>
                        {bg}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email Address"
                    placeholder="e.g. anjali.sharma@school.edu.in"
                    type="email"
                    fullWidth
                    required
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    error={!!errors.email}
                    helperText={errors.email}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Phone Number"
                    placeholder="e.g. +91 9876543210"
                    fullWidth
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Photo URL"
                    placeholder="https://..."
                    fullWidth
                    value={formData.photo_url}
                    onChange={(e) => handleChange('photo_url', e.target.value)}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Residential Address"
                    placeholder="Street, City, State, ZIP..."
                    fullWidth
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Section 2: Emergency Contact */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PhoneCall size={18} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Emergency Contact
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Emergency Contact Name"
                    placeholder="e.g. Ramesh Sharma (Spouse)"
                    fullWidth
                    value={formData.emergency_contact_name}
                    onChange={(e) => handleChange('emergency_contact_name', e.target.value)}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Emergency Contact Phone"
                    placeholder="e.g. +91 9123456780"
                    fullWidth
                    value={formData.emergency_contact_phone}
                    onChange={(e) => handleChange('emergency_contact_phone', e.target.value)}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Section 3: Employment Details */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Briefcase size={18} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Employment Details
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Employee Code"
                    placeholder="e.g. GVS-TCH-001"
                    fullWidth
                    required
                    value={formData.employee_code}
                    onChange={(e) => handleChange('employee_code', e.target.value)}
                    error={!!errors.employee_code}
                    helperText={errors.employee_code}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    select
                    label="Role"
                    fullWidth
                    required
                    error={!!errors.role_id}
                    helperText={errors.role_id}
                    value={formData.role_id}
                    onChange={(e) => handleChange('role_id', e.target.value)}
                    disabled={loadingRoles || loading}
                  >
                    {(roles || [])
                      .filter(
                        (role) =>
                          role.name?.toUpperCase() !== 'SUPER_ADMIN' &&
                          role.name?.toLowerCase() !== 'super_admin' &&
                          !role.description?.toLowerCase().includes('super admin')
                      )
                      .map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.description ||
                            (role.name
                              ? role.name.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
                              : 'Role')}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    select
                    label="Department"
                    fullWidth
                    required
                    error={!!errors.department_id}
                    helperText={errors.department_id}
                    value={formData.department_id}
                    onChange={(e) => handleChange('department_id', e.target.value)}
                    disabled={loading}
                  >
                    {(departments || []).map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    select
                    label="Designation"
                    fullWidth
                    required
                    error={!!errors.designation_id}
                    helperText={errors.designation_id}
                    value={formData.designation_id}
                    onChange={(e) => handleChange('designation_id', e.target.value)}
                    disabled={(!formData.department_id && filteredDesignations.length === 0) || loading}
                  >
                    {filteredDesignations.map((desig) => (
                      <MenuItem key={desig.id} value={desig.id}>
                        {desig.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Joining Date"
                    type="date"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={formData.joining_date}
                    onChange={(e) => handleChange('joining_date', e.target.value)}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Qualification"
                    placeholder="e.g. M.Sc Physics, B.Ed"
                    fullWidth
                    value={formData.qualification}
                    onChange={(e) => handleChange('qualification', e.target.value)}
                    disabled={loading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label="Experience (Years)"
                    type="number"
                    placeholder="0"
                    fullWidth
                    value={formData.experience_years}
                    onChange={(e) => handleChange('experience_years', e.target.value)}
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Form Actions */}
            <Divider sx={{ pt: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                disabled={loading}
                onClick={() => navigate(PATHS.STAFF_MANAGEMENT_STAFF)}
                sx={{ px: 3 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Save size={18} />}
                sx={{ px: 4, fontWeight: 600 }}
              >
                {isEdit ? 'Save Changes' : 'Enroll Staff Member'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </form>
    </Box>
  );
}
